import { ApiFetch } from '../../../shared/utils/fetch'

// Admin "create on behalf of a customer" — schema-driven intake (PE-1889/PE-1913).
// ApiFetch attaches the admin's selected merchant as x-tenant-id, so osprey-api
// proxies to the right tenant.

export type IntakeDomain = 'BOOKING' | 'CUSTOM_ORDER' | 'PACKAGE'

export interface CustomFieldDefinition {
  key: string
  type: 'text' | 'textarea' | 'number' | 'select' | 'multiselect' | 'date' | 'file' | 'url' | 'boolean'
  label: string
  helpText?: string
  required?: boolean
  validation?: { min?: number; max?: number; regex?: string; options?: string[] }
  defaultValue?: unknown
  order?: number
}
// osprey-api returns the merged array as `effective`; the storefront BFFs use `fields`.
export interface FieldSchemaResponse {
  solutionId?: string
  domain?: string
  effective?: CustomFieldDefinition[]
  fields?: CustomFieldDefinition[]
}
export function schemaFields(r: FieldSchemaResponse | undefined): CustomFieldDefinition[] {
  return r?.effective ?? r?.fields ?? []
}

export interface BookingServiceSummary { id: string; title: string }
export interface PackageListingSummary { id: string; title: string; amountMinor: number | string; currency: string }
export interface CheckoutResult { checkoutUrl?: string; [k: string]: unknown }

/**
 * POST /packages/purchase body.
 *
 * PE-2086 renamed package *templates* → *listings*. The read side followed
 * (GET /packages/listings) but the write side did not, so the modal kept posting
 * `packageTemplateId` and osprey-api's PurchasePackageDto — which declares
 * `packageListingId` with `@IsString() @IsNotEmpty()` — rejected every admin
 * "create a package order on behalf of a customer" with
 * "packageListingId should not be empty / must be a string" (PE-2287).
 * Typed (not `Record<string, unknown>`) so the key can never drift silently again.
 */
export interface PurchasePackageRequest {
  packageListingId: string
  customerEmail: string
  formData?: Record<string, unknown>
}

/** Build the POST /packages/purchase body from the modal's form state. */
export function buildPurchasePackagePayload(input: {
  packageListingId: string
  customerEmail: string
  formData?: Record<string, unknown>
}): PurchasePackageRequest {
  return {
    packageListingId: input.packageListingId.trim(),
    customerEmail: input.customerEmail.trim(),
    formData: input.formData ?? {},
  }
}

export const intakeApi = {
  getFieldSchema: (merchantId: string, solutionId: string, domain: IntakeDomain) =>
    ApiFetch.get<FieldSchemaResponse>(`/merchants/${merchantId}/field-config/${domain}?solutionId=${encodeURIComponent(solutionId)}`),

  listBookingServices: () => ApiFetch.get<unknown>(`/bookings/services`),
  createBooking: (body: Record<string, unknown>) => ApiFetch.post<unknown>(`/bookings/appointments`, body),

  listPackageListings: () => ApiFetch.get<unknown>(`/packages/listings`),
  purchasePackage: (body: PurchasePackageRequest) => ApiFetch.post<CheckoutResult>(`/packages/purchase`, body),

  createCustomOrder: (body: Record<string, unknown>) => ApiFetch.post<{ id?: string }>(`/custom-orders`, body),
}

/** Pull a list out of whatever envelope osprey-api returns. */
export function unwrapList<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[]
  const d = data as { data?: T[]; services?: T[]; listings?: T[]; templates?: T[] } | null
  // `templates` kept as a fallback: the pre-PE-2086 envelope key is still what
  // some deployed osprey-api builds return for /packages/listings.
  return (d?.data ?? d?.services ?? d?.listings ?? d?.templates ?? []) as T[]
}
